/**
 * nested_form.js — Drop-in replacement for jquery_nested_form.js
 *
 * Key differences from the original:
 *   - Reads field templates from HTML5 <template> elements (data-nested-template attribute)
 *     instead of data-blueprint / data-blueprint-id attributes.
 *   - Fires the same jQuery custom events (nested:fieldAdded, nested:fieldRemoved)
 *     so all existing event listeners in document_requirements.js, utilities.js,
 *     v4/projects.js, etc. continue working without modification.
 *   - Uses the same CSS classes (add_nested_fields, remove_nested_fields) on anchors
 *     so all programmatic .click() calls in products.js, vendor_templates.js, etc. work.
 *   - _destroy=1 is set; discarded_at injection is preserved here (moved from utilities.js).
 *
 * HTML contract:
 *   <!-- Add button -->
 *   <a class="add_nested_fields"
 *      data-association="items"
 *      data-template="items-template"
 *      data-target="#items-container">Add Item</a>
 *
 *   <!-- Template (hidden, never submitted) -->
 *   <template id="items-template">
 *     <div class="fields">
 *       <input name="order[items_attributes][NEW_RECORD][name]" ...>
 *       <input name="order[items_attributes][NEW_RECORD][_destroy]" value="false" ...>
 *       <a class="remove_nested_fields" data-association="items">Remove</a>
 *     </div>
 *   </template>
 *
 *   <!-- Container where rows are inserted -->
 *   <div id="items-container">
 *     <!-- existing .fields rows rendered by Rails fields_for -->
 *   </div>
 *
 * The placeholder string "NEW_RECORD" is replaced by a millisecond timestamp on insert.
 */

(function ($) {
    'use strict';

    var PLACEHOLDER = 'NEW_RECORD';

    function newId() {
        return new Date().getTime();
    }

    /**
     * Add a new set of nested fields.
     * Reads content from a <template> element identified by data-template attribute.
     */
    function addFields(e) {
        var $link = $(e.currentTarget);
        var assoc = $link.data('association');
        var templateId = $link.data('template');
        var $template = templateId ? $('#' + templateId) : $link.siblings('template').first();

        if (!$template.length) {
            console.warn('[nested_form] No <template> found for association:', assoc, '(id:', templateId, ')');
            return false;
        }

        // Clone template content (HTML5 <template> element)
        var id = newId();
        var regexp = new RegExp(PLACEHOLDER, 'g');
        var content;

        // Support both HTML5 <template> elements and legacy data-blueprint (backward compat)
        if ($template.is('template') || $template[0].tagName === 'TEMPLATE') {
            content = $template[0].innerHTML.replace(regexp, id);
        } else {
            // Fallback: original jquery_nested_form data-blueprint on a hidden div
            content = $template.data('blueprint');
            if (!content) {
                console.warn('[nested_form] Template element has no content for:', assoc);
                return false;
            }
            content = $.trim(content.replace(regexp, id));
        }

        var $field = insertFields(content, assoc, $link);

        // Fire same jQuery events as original jquery_nested_form.js
        $field
            .trigger({ type: 'nested:fieldAdded', field: $field })
            .trigger({ type: 'nested:fieldAdded:' + assoc, field: $field });

        return false;
    }

    /**
     * Insert rendered HTML before the add link (or into a data-target container).
     */
    function insertFields(content, assoc, $link) {
        var target = $link.data('target');
        if (target) {
            return $(content).appendTo($(target));
        } else {
            return $(content).insertBefore($link);
        }
    }

    /**
     * Remove a set of nested fields.
     * Sets _destroy=1 (hidden field immediately preceding the remove link),
     * hides the .fields wrapper, and injects discarded_at (soft-delete pattern).
     */
    function removeFields(e) {
        var $link = $(e.currentTarget);
        var assoc = $link.data('association');
        var $field = $link.closest('.fields');

        // Set _destroy = 1 (hidden field immediately before the remove link, Rails convention)
        var $destroyInput = $link.prevAll('input[type=hidden]').filter(function () {
            return this.name && this.name.indexOf('_destroy') !== -1;
        }).first();

        if (!$destroyInput.length) {
            // Fallback: find _destroy anywhere in the .fields block
            $destroyInput = $field.find('input[type=hidden]').filter(function () {
                return this.name && this.name.indexOf('_destroy') !== -1;
            }).first();
        }

        $destroyInput.val('1');

        // Inject discarded_at alongside _destroy (soft-delete pattern from v4/utilities.js)
        // This preserves the existing business logic that was previously in utilities.js.
        if ($destroyInput.length) {
            var discardedName = $destroyInput.attr('name').replace('_destroy', 'discarded_at');
            var discardedId = ($destroyInput.attr('id') || '').replace('_destroy', 'discarded_at');
            if (!$field.find('input[name="' + discardedName + '"]').length) {
                $('<input type="hidden"/>')
                    .attr('id', discardedId)
                    .attr('name', discardedName)
                    .val(new Date().toISOString())
                    .appendTo($destroyInput.parent());
            }
        }

        $field.hide();

        // Fire same jQuery events as original jquery_nested_form.js
        $field
            .trigger({ type: 'nested:fieldRemoved', field: $field })
            .trigger({ type: 'nested:fieldRemoved:' + assoc, field: $field });

        return false;
    }

    // Delegate in the same way as the original, preserving click handling for all dynamic forms.
    $(document)
        .on('click', 'form a.add_nested_fields', addFields)
        .on('click', 'form a.remove_nested_fields', removeFields);

    // Expose on window for any code that accesses nestedFormEvents directly
    window.nestedFormEvents = {
        addFields: addFields,
        removeFields: removeFields,
        newId: newId
    };

})(jQuery);
